import type {
  AnnualBehaviorNote,
  AnnualTimetableCell,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import {
  patchProgramPresentationListStatsCache,
  removeProgramPresentationListStatsCacheKeys,
  type SlideOwnerStats,
} from '../utils/programPresentationListStatsCache';
import {
  clearAllPresentationAuditSnapshotCachesForWorkspace,
  clearPresentationAuditSnapshotCachesForGrade,
  clearProgramPresentationAuditSnapshotCache,
} from '../utils/presentationAuditSnapshotCache';
import { clearAnnualBehaviorNotesCache } from '../services/annualBehaviorNotesService';
import { buildAnnualCellMap } from '../utils/annualTimetable';
import { schoolKeys } from './keys';
import { queryClient } from './queryClient';

export function setProgramProgressCellsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramProgressCell[]
): void {
  queryClient.setQueryData(
    schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId),
    rows
  );
}

export function setProgramFixedRowsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramFixedContentRow[]
): void {
  queryClient.setQueryData(
    schoolKeys.programFixedRows(workspaceId, academicYear, termNumber, gradeId),
    rows
  );
}

export function setProgramScopedContentRowsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: string,
  scopeKey: string,
  rows: ProgramScopedContentRow[]
): void {
  queryClient.setQueryData(
    schoolKeys.programScopedContentRows(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    ),
    rows
  );
}

export function getProgramScopedContentRowsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: string,
  scopeKey: string
): ProgramScopedContentRow[] | undefined {
  return queryClient.getQueryData<ProgramScopedContentRow[]>(
    schoolKeys.programScopedContentRows(workspaceId, academicYear, termNumber, gradeId, scopeType, scopeKey)
  );
}

/** 연간진도표·대시보드가 쓰는 학년·학기 단위 scoped 행 캐시 */
export function setProgramScopedRowsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: ProgramScopedContentRow[]
): void {
  queryClient.setQueryData(
    schoolKeys.programScopedRows(workspaceId, academicYear, termNumber, gradeId),
    rows
  );
}

export function getProgramScopedRowsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): ProgramScopedContentRow[] | undefined {
  return queryClient.getQueryData<ProgramScopedContentRow[]>(
    schoolKeys.programScopedRows(workspaceId, academicYear, termNumber, gradeId)
  );
}

export function invalidateProgramProgressCellsQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.programProgressCells(workspaceId, academicYear, termNumber, gradeId),
  });
}

export function invalidateProgramFixedRowsQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.programFixedRows(workspaceId, academicYear, termNumber, gradeId),
  });
}

export function invalidateProgramScopedRowsQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.programScopedRows(workspaceId, academicYear, termNumber, gradeId),
  });
}

export function invalidateProgramScopedContentRowsQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeType: string,
  scopeKey: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.programScopedContentRows(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeType,
      scopeKey
    ),
  });
}

export function invalidateAnnualTimetableCellsQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.annualTimetableCells(workspaceId, academicYear, termNumber, gradeId),
  });
}

export function invalidateAnnualProgressMetaQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.annualProgressMeta(workspaceId, academicYear, termNumber, gradeId),
  });
}

function annualCellNaturalKey(row: Pick<AnnualTimetableCell, 'monday_iso' | 'weekday_key' | 'slot_index'>): string {
  return `${row.monday_iso}|${row.weekday_key}|${row.slot_index}`;
}

export function mergeAnnualTimetableCellsQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  savedRows: AnnualTimetableCell[]
): void {
  const key = schoolKeys.annualTimetableCells(workspaceId, academicYear, termNumber, gradeId);
  const gradeRows = savedRows.filter((r) => r.grade_id === gradeId && r.term_number === termNumber);
  if (gradeRows.length === 0) return;

  const existing = queryClient.getQueryData<AnnualTimetableCell[]>(key);
  if (!existing?.length) {
    queryClient.setQueryData(key, gradeRows);
    return;
  }

  const merged = new Map<string, AnnualTimetableCell>();
  for (const row of existing) {
    merged.set(annualCellNaturalKey(row), row);
  }
  for (const row of gradeRows) {
    merged.set(annualCellNaturalKey(row), row);
  }
  queryClient.setQueryData(key, Array.from(merged.values()));
}

function annualTimetableCellsBulkGradeIdsKey(gradeIds: string[]): string {
  return [...gradeIds].filter(Boolean).sort().join('\u0001');
}

export function mergeAnnualProgressTimetableCellsBulkQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[],
  savedRows: AnnualTimetableCell[]
): void {
  const grades = Array.from(new Set(gradeIds.filter(Boolean)));
  if (!grades.length) return;
  const patchRows = savedRows.filter(
    (row) => row.term_number === termNumber && grades.includes(row.grade_id)
  );
  if (patchRows.length === 0) return;

  const key = [
    'school',
    workspaceId,
    'annualTimetableCellsBulk',
    academicYear,
    termNumber,
    annualTimetableCellsBulkGradeIdsKey(grades),
  ] as const;
  const patchMap = buildAnnualCellMap(patchRows);
  const existing = queryClient.getQueryData<Record<string, AnnualTimetableCell>>(key);
  queryClient.setQueryData(key, existing ? { ...existing, ...patchMap } : patchMap);
}

export function invalidateAnnualProgressTimetableCellsBulkQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeIds: string[]
): void {
  void queryClient.invalidateQueries({
    queryKey: [
      'school',
      workspaceId,
      'annualTimetableCellsBulk',
      academicYear,
      termNumber,
      annualTimetableCellsBulkGradeIdsKey(gradeIds),
    ] as const,
  });
}

export type AnnualTimetableSaveScope = {
  workspaceId: string;
  academicYear: number;
  termNumbers: Array<1 | 2>;
  gradeIds: string[];
};

/** 연간시간표 저장 이벤트: 셀 본문 병합은 upsert 직후 수행됨. 메타·프로그램 RQ는 건드리지 않음. */
export function applyAnnualTimetableSaveQueryUpdates(scope: AnnualTimetableSaveScope): void {
  const { workspaceId, academicYear, termNumbers, gradeIds } = scope;
  const terms = Array.from(new Set(termNumbers));
  const grades = Array.from(new Set(gradeIds.filter(Boolean)));

  for (const termNumber of terms) {
    for (const gradeId of grades) {
      void queryClient.invalidateQueries({
        queryKey: schoolKeys.annualTimetableCells(workspaceId, academicYear, termNumber, gradeId),
        refetchType: 'none',
      });
    }
    /** 연간진도표용 묶음 키(학년 묶음별)도 낡음 표시 — 다음에 화면이 열릴 때 다시 읽는다 */
    void queryClient.invalidateQueries({
      queryKey: ['school', workspaceId, 'annualTimetableCellsBulk', academicYear, termNumber],
      refetchType: 'none',
    });
  }
}

export function patchPresentationOwnerStatsCache(
  workspaceId: string,
  patch: Map<string, SlideOwnerStats> | Record<string, SlideOwnerStats>
): void {
  patchProgramPresentationListStatsCache(workspaceId, patch);
}

export function setPresentationOwnerStatsQueryData(
  workspaceId: string,
  ownerKeysSignature: string,
  statsMap: Map<string, SlideOwnerStats>
): void {
  queryClient.setQueryData(
    schoolKeys.presentationOwnerStats(workspaceId, ownerKeysSignature),
    statsMap
  );
}

export function invalidatePresentationOwnerStatsQueries(workspaceId: string): void {
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'school' &&
        key[1] === workspaceId &&
        key[2] === 'presentationOwnerStats'
      );
    },
  });
}

export function removePresentationOwnerStatsCacheKeys(
  workspaceId: string,
  ownerKeys: string[]
): void {
  removeProgramPresentationListStatsCacheKeys(workspaceId, ownerKeys);
  invalidatePresentationOwnerStatsQueries(workspaceId);
}

export function invalidateAnnualPresentationAuditQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  clearPresentationAuditSnapshotCachesForGrade(workspaceId, academicYear, termNumber, gradeId);
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.annualPresentationAudit(workspaceId, academicYear, termNumber, gradeId),
  });
}

export function invalidateProgramPresentationAuditQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  scopeSignature: string
): void {
  clearProgramPresentationAuditSnapshotCache(
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
    scopeSignature
  );
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.programPresentationAudit(
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      scopeSignature
    ),
  });
}

export function invalidateAllPresentationAuditQueriesForGrade(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  invalidateAnnualPresentationAuditQuery(workspaceId, academicYear, termNumber, gradeId);
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'school' &&
        key[1] === workspaceId &&
        key[2] === 'programPresentationAudit' &&
        key[3] === academicYear &&
        key[4] === termNumber &&
        key[5] === gradeId
      );
    },
  });
}

export function invalidateAllPresentationAuditQueriesForWorkspace(workspaceId: string): void {
  clearAllPresentationAuditSnapshotCachesForWorkspace(workspaceId);
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === 'school' &&
        key[1] === workspaceId &&
        (key[2] === 'annualPresentationAudit' || key[2] === 'programPresentationAudit')
      );
    },
  });
}

export function setAnnualBehaviorNotesQueryData(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string,
  rows: AnnualBehaviorNote[]
): void {
  queryClient.setQueryData(
    schoolKeys.annualBehaviorNotes(workspaceId, academicYear, termNumber, gradeId),
    rows
  );
}

export function invalidateAnnualBehaviorNotesQuery(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): void {
  clearAnnualBehaviorNotesCache(workspaceId, academicYear, termNumber, gradeId);
  void queryClient.invalidateQueries({
    queryKey: schoolKeys.annualBehaviorNotes(workspaceId, academicYear, termNumber, gradeId),
  });
}
